// Video block for case studies: an uploaded file (native player) or a YouTube / Vimeo link (embed behind the media consent gate).
// window.VideoLink.parse(u) -> { kind: 'file' | 'youtube' | 'vimeo', id?, hash?, start?, vertical? } or null
// Props: u (upload path or link), c (caption), download (an uploaded file may be saved; default off)
// The server accepts the same patterns (VIDEO_FILE / VIDEO_LINK in apps/portal/portfolio-store.js).
(function () {
  const { Box, Typography } = MaterialUI;
  const { useState, useEffect, useRef } = React;

  const FILE = /^\/uploads\/portfolio\/videos\/[\w.-]+\.(mp4|webm|mov)$/;
  const YOUTUBE = /^https:\/\/(?:(?:www\.|m\.)?youtube(?:-nocookie)?\.com\/(?:watch\?(?:[^\s"'<>]*&)?v=|embed\/|(shorts)\/|live\/)|youtu\.be\/)([\w-]{6,20})/;
  const VIMEO = /^https:\/\/(?:www\.|player\.)?vimeo\.com\/(?:video\/)?(\d{5,12})(?:\/([0-9a-f]{6,20}))?/;

  // YouTube's t / start parameter: "90", "90s" or "1m30s" -> seconds
  const seconds = (t) => {
    const m = String(t || '').match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/);
    return m ? (Number(m[1] || 0) * 3600) + (Number(m[2] || 0) * 60) + Number(m[3] || 0) : 0;
  };

  function parse(u) {
    const s = String(u || '').trim();
    if (!s) return null;
    if (FILE.test(s)) return { kind: 'file' };
    let m = s.match(YOUTUBE);
    if (m) {
      const q = (s.split('?')[1] || '').split('#')[0];
      const params = new URLSearchParams(q);
      return { kind: 'youtube', id: m[2], start: seconds(params.get('t') || params.get('start')), vertical: !!m[1] };
    }
    m = s.match(VIMEO);
    if (m) return { kind: 'vimeo', id: m[1], hash: m[2] || new URLSearchParams((s.split('?')[1] || '').split('#')[0]).get('h') || '' };
    return null;
  }

  const embedSrc = (v) => (v.kind === 'youtube'
    ? `https://www.youtube-nocookie.com/embed/${v.id}?rel=0${v.start ? `&start=${v.start}` : ''}`
    : `https://player.vimeo.com/video/${v.id}?dnt=1${v.hash ? `&h=${v.hash}` : ''}`);

  // ---- uploaded files: adaptive streaming --------------------------------------------------------------------------------
  // The server makes 480p..4K renditions of every upload (apps/portal/video-hls.js). While none exist the original file plays as before.
  // The video's streams and poster (/api/portfolio/video-info, video-hls.js): a small JSON, kept per upload once the streams exist
  const infoCache = new Map();
  const loadInfo = u => {
    if (infoCache.has(u)) return infoCache.get(u);
    const p = fetch(`/api/portfolio/video-info?u=${encodeURIComponent(u)}`).then(r => (r.ok ? r.json() : null)).catch(() => null)
      .then(d => { if (!d || !d.hls) infoCache.delete(u); return d; });
    infoCache.set(u, p);
    return p;
  };

  // An uploaded video on the page is only its poster (no video, stream or metadata is fetched): a click opens it in the lightbox with
  // the Material 3 controls (VideoLightbox, VideoPlayer.js, media bundle). Without a poster yet (streams still being made) a play tile.
  function FileVideo({ u, download, frame, title }) {
    const { GoogleIcon } = window.Components;
    const [info, setInfo] = useState(undefined); // undefined = asking, null = no streams
    const [open, setOpen] = useState(null);       // the thumbnail element (the lightbox grows out of it)
    useEffect(() => { let live = true; loadInfo(u).then(d => { if (live) setInfo(d && d.hls ? d : null); }); return () => { live = false; }; }, [u]);
    const lv = info && (info.levels || [])[0];
    const ratio = lv && lv.w && lv.h ? `${lv.w} / ${lv.h}` : '16 / 9';
    const poster = info && info.poster;
    const Lightbox = window.Components.VideoLightbox;
    return (
      <>
        <Box component="button" type="button" onClick={e => setOpen(e.currentTarget)} aria-label={`Play video${title ? `: ${title}` : ''}`}
          sx={{ ...frame, position: 'relative', display: 'block', width: '100%', p: 0, border: 0, cursor: 'pointer', aspectRatio: ratio, maxHeight: '80vh',
            '&:hover .vb-play, &:focus-visible .vb-play': { transform: 'scale(1.08)' }, '&:focus-visible': { outline: '3px solid var(--m3-secondary)', outlineOffset: 2 } }}>
          {poster && <Box component="img" src={poster} alt="" loading="lazy" decoding="async" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
            <Box className="vb-play" sx={{ width: 64, height: 64, borderRadius: 'var(--m3-shape-large)', display: 'grid', placeItems: 'center', bgcolor: 'var(--m3-primary-container)', color: 'var(--m3-on-primary-container)', boxShadow: 'var(--m3-elevation-2)', transition: 'transform var(--m3-duration-short, 150ms) var(--m3-easing-standard, ease)' }}>
              <GoogleIcon name="play_arrow" size={36} filled />
            </Box>
          </Box>
        </Box>
        {open && Lightbox && <Lightbox u={u} title={title} info={info || null} download={download} origin={open} onClose={() => setOpen(null)} />}
      </>
    );
  }

  function VideoBlock({ u, c, download = false }) {
    const { ConsentGate, ImagePlaceholder } = window.Components;
    const v = parse(u);
    const frame = { borderRadius: 'var(--m3-shape-large)', overflow: 'hidden', backgroundColor: 'var(--m3-surface-container-highest)' };
    let player;
    if (!v) {
      player = ImagePlaceholder ? <ImagePlaceholder label="video" /> : null;
    } else if (v.kind === 'file') {
      // #t=0.1 makes browsers paint the first frame as the poster
      // No download unless the block allows it: the player hides its download button and the right-click menu is off (the server also
      // refuses the file when it is opened or fetched outside a player, see videoGuard in portfolio-store.js)
      player = <FileVideo u={u} download={download} frame={frame} title={c} />;
    } else {
      const service = v.kind === 'youtube' ? 'YouTube' : 'Vimeo';
      player = (
        // Shorts are vertical: a narrower 9:16 frame instead of 16:9
        <Box sx={{ ...frame, position: 'relative', aspectRatio: v.vertical ? '9 / 16' : '16 / 9', ...(v.vertical ? { maxWidth: 360, mx: 'auto' } : {}) }}>
          <ConsentGate service={service} href={u} fill>
            <Box component="iframe" src={embedSrc(v)} title={c || `${service} video`} loading="lazy" allowFullScreen
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} />
          </ConsentGate>
        </Box>
      );
    }
    return (
      <Box component="figure" sx={{ m: 0, my: 2 }}>
        {player}
        {c && <Typography component="figcaption" variant="body2" sx={{ display: 'block', mt: 1, color: 'var(--m3-secondary)', font: 'var(--m3-type-body-small)' }}>{c}</Typography>}
      </Box>
    );
  }

  window.VideoLink = { parse };
  window.Components = window.Components || {};
  window.Components.VideoBlock = VideoBlock;
})();
